import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  AlertTriangle, Clock, Calendar, Package, Search,
  ChevronDown, ChevronUp, Filter, ArrowUpDown
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Urgency config
const URGENCY = {
  expired:  { label: 'Expired',         color: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200',    icon: AlertTriangle, badge: 'bg-red-100 text-red-800' },
  days_30:  { label: 'Within 30 Days',  color: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', icon: Clock,         badge: 'bg-orange-100 text-orange-800' },
  days_60:  { label: 'Within 60 Days',  color: 'bg-amber-500',  text: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200',  icon: Calendar,      badge: 'bg-amber-100 text-amber-800' },
  days_90:  { label: 'Within 90 Days',  color: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: Calendar,      badge: 'bg-yellow-100 text-yellow-800' },
};

const formatDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const daysUntil = (d) => {
  if (!d) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const exp = new Date(d);
  exp.setHours(0, 0, 0, 0);
  return Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
};

export default function ExpiryAlertsPage() {
  const { token, currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortField, setSortField] = useState('days_remaining');
  const [sortDir, setSortDir] = useState('asc');
  const [expandedGroups, setExpandedGroups] = useState({ expired: true, days_30: true, days_60: true, days_90: true });

  useEffect(() => {
    if (currentOrg?.id) fetchAlerts();
  }, [currentOrg?.id]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/inventory/expiry-alerts?organization_id=${currentOrg.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch expiry alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Flatten all items for the table view
  const allItems = useMemo(() => {
    if (!data) return [];
    const items = [];
    for (const [urgency, list] of Object.entries(data.groups || {})) {
      for (const item of list) {
        items.push({ ...item, urgency });
      }
    }
    return items;
  }, [data]);

  // Filter + search + sort
  const filtered = useMemo(() => {
    let result = allItems;
    if (activeFilter !== 'all') {
      result = result.filter(i => i.urgency === activeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.item_name?.toLowerCase().includes(q) ||
        i.batch_number?.toLowerCase().includes(q) ||
        i.item_code?.toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'days_remaining') {
        va = va ?? 9999; vb = vb ?? 9999;
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return result;
  }, [allItems, activeFilter, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const SortIcon = ({ field }) => (
    <ArrowUpDown className={`w-3 h-3 ml-1 inline ${sortField === field ? 'text-blue-500' : 'text-gray-400'}`} />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 text-sm">Loading expiry alerts...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-500">Failed to load expiry data. Please try again.</p>
      </div>
    );
  }

  const counts = data.summary || {};

  return (
    <div className="space-y-6" data-testid="expiry-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expiry Alerts</h1>
          <p className="text-sm text-gray-500 mt-1">Monitor batch expiry dates across your inventory</p>
        </div>
        <Button onClick={fetchAlerts} variant="outline" size="sm" data-testid="refresh-alerts-btn">
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="expiry-summary-cards">
        {Object.entries(URGENCY).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count = counts[key] || 0;
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              onClick={() => setActiveFilter(f => f === key ? 'all' : key)}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                isActive ? `${cfg.border} ${cfg.bg} shadow-md scale-[1.02]` : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
              }`}
              data-testid={`summary-card-${key}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${cfg.color} bg-opacity-15 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${cfg.text}`} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-xs text-gray-500 font-medium">{cfg.label}</div>
                </div>
              </div>
              {isActive && (
                <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${cfg.color}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by item name, code, or batch..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="expiry-search-input"
          />
        </div>
        {activeFilter !== 'all' && (
          <Button variant="ghost" size="sm" onClick={() => setActiveFilter('all')} className="text-xs">
            <Filter className="w-3 h-3 mr-1" /> Clear Filter
          </Button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Data Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No expiry alerts found</p>
          <p className="text-gray-400 text-sm mt-1">
            {activeFilter !== 'all' ? 'Try changing the filter or search term.' : 'All batches have expiry dates beyond 90 days.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expiry-alerts-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('urgency')}>
                    Status <SortIcon field="urgency" />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('item_name')}>
                    Item <SortIcon field="item_name" />
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Batch #</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('expiry_date')}>
                    Expiry Date <SortIcon field="expiry_date" />
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('days_remaining')}>
                    Days Left <SortIcon field="days_remaining" />
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('quantity')}>
                    Qty <SortIcon field="quantity" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => {
                  const cfg = URGENCY[item.urgency] || URGENCY.days_90;
                  return (
                    <tr key={`${item.item_id}-${item.batch_id}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`expiry-row-${idx}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
                          {item.days_remaining !== null && item.days_remaining <= 0 ? 'Expired' : cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800 truncate max-w-[300px]">{item.item_name}</div>
                        <div className="text-xs text-gray-400 font-mono">{item.item_code}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-600">{item.batch_number}</td>
                      <td className="px-4 py-3 text-gray-700">{formatDate(item.expiry_date)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${item.days_remaining <= 0 ? 'text-red-600' : item.days_remaining <= 30 ? 'text-orange-600' : item.days_remaining <= 60 ? 'text-amber-600' : 'text-yellow-600'}`}>
                          {item.days_remaining <= 0 ? `${Math.abs(item.days_remaining)}d overdue` : `${item.days_remaining}d`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700">{item.quantity}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
